/**
 * Every sentence the start page shows. Kept together so the page cannot pick
 * its words from a server message, and so one test can read them all.
 */
import type { ApiError } from "@/lib/api";

export function landingIntro(employer: string, jobTitle: string | null): string {
  const lead = jobTitle
    ? `You've applied for ${jobTitle} at ${employer}.`
    : `You've applied to ${employer}.`;
  return `${lead} If you'd like, you can add a Trust Checkpoint to your application: a few short steps that let the hiring team see verified facts you choose to share.`;
}

export const LANDING_OPTIONAL = "It's optional. Your application stands either way.";

export const LANDING_EMAIL_NOTE = "To find your application we'll confirm the email address you applied with.";

export const SEND_ME_A_CODE = "Or send me a code";

export const EMAIL_LABEL = "The email address you applied with";

export function codeRequested(employer: string): string {
  return `If that address matches an application at ${employer}, we've sent it a code. Enter it below. Check your spam folder if it hasn't arrived in a minute.`;
}

export const NOT_PROVEN =
  "That sign-in confirmed your account but not the email address on your application. Send yourself a code instead.";

export const LOOKING = "Looking for your application.";

export const NO_MATCH =
  "We couldn't match that address to a single application for this role just now. If one matches later, we'll email your link to this address. Give it ten minutes and check your spam folder. Nothing else is needed from you now.";

export function matched(jobTitle: string | null, employer: string | null): string {
  const found = jobTitle && employer
    ? `Found your application for ${jobTitle} at ${employer}.`
    : employer
      ? `Found your application at ${employer}.`
      : "Found your application.";
  return `${found} Your email is confirmed. Let's continue.`;
}

/** The code panel's own words (`EmailCodePanel.tsx`), kept the same here. */
export const WRONG_CODE = { title: "That code didn't match", detail: "Check the code and try again, or send a new one." };
export const CODE_EXPIRED = { title: "That code expired", detail: "Send a new code to try again." };
export const TOO_MANY_ATTEMPTS = { title: "Too many attempts", detail: "Send a new code to try again." };

export const UNAVAILABLE = "This page isn't set up for this employer yet. Your application stands either way.";

export const INVALID_EMAIL = "That doesn't look like an email address. Check it and try again.";

export const RATE_LIMITED =
  "We've had several requests from this connection in the last hour. Please try again later. If you applied, the hiring team's confirmation email is unaffected.";

export const OUR_SIDE =
  "Something went wrong on our side and we couldn't take your request just now. Please try again in a few minutes.";

export type StartNotice =
  | { kind: "unavailable" }
  | { kind: "invalid_email" }
  | { kind: "rate_limited" }
  | { kind: "code_expired" }
  | { kind: "too_many_attempts" }
  | { kind: "wrong_code" }
  | { kind: "not_proven" }
  | { kind: "our_side" };

/**
 * Which notice an error gets, by its `code` alone. Anything the page does not
 * know (a 5xx, no connection, a code it has no words for) is "our side".
 */
export function noticeFor(err: unknown): StartNotice {
  const code = (err as ApiError | null)?.code;
  switch (code) {
    case "START_UNAVAILABLE":
      return { kind: "unavailable" };
    case "INVALID_EMAIL":
      return { kind: "invalid_email" };
    case "START_RATE_LIMITED":
      return { kind: "rate_limited" };
    case "CODE_EXPIRED":
      return { kind: "code_expired" };
    case "TOO_MANY_ATTEMPTS":
      return { kind: "too_many_attempts" };
    default:
      return { kind: "our_side" };
  }
}

/** The words for a notice: a title where the code panel uses one, then the text. */
export function noticeText(notice: StartNotice): { title?: string; detail: string } {
  switch (notice.kind) {
    case "unavailable":
      return { detail: UNAVAILABLE };
    case "invalid_email":
      return { detail: INVALID_EMAIL };
    case "rate_limited":
      return { detail: RATE_LIMITED };
    case "code_expired":
      return CODE_EXPIRED;
    case "too_many_attempts":
      return TOO_MANY_ATTEMPTS;
    case "wrong_code":
      return WRONG_CODE;
    case "not_proven":
      return { detail: NOT_PROVEN };
    case "our_side":
      return { detail: OUR_SIDE };
  }
}
